import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import {
    getFilesByEntity,
    getFileById,
    uploadFileToCampusFileSystem,
    deleteFile,
    downloadCampusFile
} from '../../services/CampusFileSystemService';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    InsertDriveFile as FileIcon,
    Download as DownloadIcon,
    CheckCircle as ActiveIcon,
    HourglassEmpty as PendingIcon,
    Error as ErrorIcon,
    Search as SearchIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import './DocumentsTab.css';

export default function DocumentsTab({ entityId, entityType = 'USER', orgId: propOrgId }) {
    const { token, user } = useAuth();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('ALL');

    // Upload Modal State
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadCategory, setUploadCategory] = useState('OTHER');
    const [uploadDocType, setUploadDocType] = useState('');
    const [uploadRemarks, setUploadRemarks] = useState('');

    const fileInputRef = useRef(null);

    const activeEntityId = String(entityId || user?.userId || 'general');
    const activeOrgId = String(propOrgId || user?.orgId || 'DEFAULT');

    // Fetch Entity Documents
    const fetchDocuments = useCallback(async () => {
        if (!activeEntityId || !token) return;
        setLoading(true);
        try {
            const data = await getFilesByEntity(activeEntityId, token, 0, 100);
            const items = Array.isArray(data) ? data : data.items || data.content || [];
            setFiles(items);
        } catch (err) {
            console.error('Failed to load documents:', err);
            toast.error('Failed to load documents.');
        } finally {
            setLoading(false);
        }
    }, [activeEntityId, token]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleOpenUploadModal = () => {
        setSelectedFile(null);
        setUploadCategory('OTHER');
        setUploadDocType('');
        setUploadRemarks('');
        setIsUploadModalOpen(true);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    // Submit Document Upload
    const handleSubmitUpload = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            toast.error("Please select a file to upload.");
            return;
        }

        setUploading(true);
        toast.info(`Uploading ${selectedFile.name}...`);

        try {
            await uploadFileToCampusFileSystem(selectedFile, {
                entityType: entityType,
                entityId: activeEntityId,
                orgId: activeOrgId,
                category: uploadCategory || 'OTHER',
                documentType: uploadDocType.trim() || undefined,
                remarks: uploadRemarks.trim() || undefined,
                uploadedBy: user?.userId || 'system',
                token: token
            });

            toast.success(`Successfully uploaded ${selectedFile.name}!`);
            setIsUploadModalOpen(false);
            setSelectedFile(null);
            fetchDocuments();
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error(err.message || 'Upload failed, please try again.');
        } finally {
            setUploading(false);
        }
    };

    // Handle Document Download (URL Masked + 5-Min Expiration Protection + CORS/Direct Fallback)
    const handleDownload = async (doc) => {
        const fileId = doc.fileId || doc.id;
        const displayName = doc.filename || doc.fileName || doc.name || doc.originalFilename || doc.originalFileName || doc.title || 'Document';

        if (!fileId) {
            toast.error("Invalid file ID.");
            return;
        }

        try {
            toast.info(`Downloading ${displayName}...`);
            await downloadCampusFile(fileId, displayName, token);
            toast.success(`Downloaded ${displayName}`);
        } catch (err) {
            console.error("Failed to download file:", err);
            toast.error(err.message || "Could not download document.");
        }
    };

    // Handle Document Deletion
    const handleDeleteDocument = async (fileId, filename) => {
        if (!window.confirm(`Are you sure you want to permanently delete "${filename}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await deleteFile(fileId, token);
            toast.success(`Deleted ${filename}`);
            setFiles(prev => prev.filter(f => f.fileId !== fileId));
        } catch (err) {
            console.error('Delete failed:', err);
            toast.error('Failed to delete file.');
        }
    };

    // Filter Documents
    const filteredFiles = files.filter(f => {
        const name = (f.filename || f.fileName || f.name || f.originalFilename || f.originalFileName || f.title || f.documentName || '').toLowerCase();
        const docType = (f.documentType || '').toLowerCase();
        const remarks = (f.remarks || '').toLowerCase();
        const q = searchQuery.toLowerCase().trim();
        const matchesSearch = !q || name.includes(q) || docType.includes(q) || remarks.includes(q);
        const matchesCategory = selectedCategory === 'ALL' || f.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="documents-tab-container">
            {/* Toolbar */}
            <div className="documents-toolbar">
                <div className="search-and-filter">
                    <div className="doc-search-box">
                        <SearchIcon className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search documents by name, type, remarks..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <select
                        className="doc-category-select"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        <option value="ALL">All Categories</option>
                        <option value="OTHER">Other Documents</option>
                        <option value="ACADEMIC">Academic Records</option>
                        <option value="PERSONAL_ID">Personal ID / Proof</option>
                        <option value="ASSIGNMENT">Assignments</option>
                        <option value="STUDY_MATERIAL">Study Material</option>
                        <option value="CERTIFICATE">Certificates</option>
                        <option value="FINANCIAL">Financial / Receipts</option>
                    </select>
                </div>

                <div className="doc-toolbar-actions">
                    <button className="refresh-doc-btn" onClick={fetchDocuments} disabled={loading}>
                        <RefreshIcon className={loading ? 'spin' : ''} />
                        <span>Refresh</span>
                    </button>

                    <button className="upload-doc-btn" onClick={handleOpenUploadModal} disabled={uploading}>
                        <UploadIcon />
                        <span>Upload Document</span>
                    </button>
                </div>
            </div>

            {/* Documents List Table */}
            {loading && files.length === 0 ? (
                <div className="doc-loader-container">
                    <div className="spinner"></div>
                    <p>Loading documents...</p>
                </div>
            ) : filteredFiles.length === 0 ? (
                <div className="doc-empty-state">
                    <FileIcon className="empty-icon" />
                    <h4>No Documents Found</h4>
                    <p>Upload new documents using the Upload Document button.</p>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="documents-list-table">
                        <thead>
                            <tr>
                                <th style={{ width: '40px' }}>#</th>
                                <th>Document Name & Type</th>
                                <th>Category</th>
                                <th>Remarks / Notes</th>
                                <th>Size</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredFiles.map((doc, index) => {
                                const status = doc.status || 'ACTIVE';
                                const isPending = status === 'PENDING_UPLOAD' || status === 'VALIDATING';
                                const isRejected = status === 'REJECTED';
                                const displayName = doc.filename || doc.fileName || doc.name || doc.originalFilename || doc.originalFileName || doc.title || doc.documentName || doc.documentType || (doc.fileId ? `Document (${doc.fileId.substring(0, 8)})` : 'Document');

                                return (
                                    <tr key={doc.fileId || doc.id || index}>
                                        <td className="col-index">{index + 1}</td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <FileIcon className="doc-type-icon" style={{ fontSize: '20px' }} />
                                                <div>
                                                    <span className="doc-filename-link" title={displayName}>{displayName}</span>
                                                    {doc.documentType && doc.documentType !== displayName && (
                                                        <div style={{ fontSize: '12px', color: '#014486', fontWeight: 600 }}>
                                                            Type: {doc.documentType}
                                                        </div>
                                                    )}
                                                    {isRejected && doc.rejectionReason && (
                                                        <div className="doc-rejection-msg" style={{ marginTop: '2px' }}>
                                                            ⚠️ {doc.rejectionReason}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="category-pill">{doc.category || 'OTHER'}</span>
                                        </td>
                                        <td style={{ fontSize: '13px', color: '#475569', maxWidth: '200px' }}>
                                            {doc.remarks || doc.description || '-'}
                                        </td>
                                        <td style={{ color: '#64748b', fontSize: '13px' }}>
                                            {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : 'N/A'}
                                        </td>
                                        <td>
                                            <div className={`doc-status-badge ${status.toLowerCase()}`}>
                                                {status === 'ACTIVE' && <ActiveIcon fontSize="inherit" />}
                                                {isPending && <PendingIcon fontSize="inherit" className="spin" />}
                                                {isRejected && <ErrorIcon fontSize="inherit" />}
                                                <span>{status}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                <button
                                                    className="doc-action-btn download"
                                                    onClick={() => handleDownload(doc)}
                                                    title="View / Download Document"
                                                >
                                                    <DownloadIcon fontSize="small" />
                                                    <span>Download</span>
                                                </button>

                                                <button
                                                    className="doc-action-btn delete-icon-only"
                                                    onClick={() => handleDeleteDocument(doc.fileId, doc.filename)}
                                                    title="Permanently Delete Document"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Upload Document Modal */}
            {isUploadModalOpen && (
                <>
                    <div className="doc-modal-backdrop" onClick={() => !uploading && setIsUploadModalOpen(false)} />
                    <div className="doc-upload-modal">
                        <div className="doc-modal-header">
                            <h3>Upload New Document</h3>
                            <button 
                                className="doc-modal-close" 
                                onClick={() => !uploading && setIsUploadModalOpen(false)}
                                disabled={uploading}
                            >
                                <CloseIcon fontSize="small" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmitUpload} className="doc-modal-body">
                            {/* File Selector */}
                            <div className="doc-form-group">
                                <label>Select File <span style={{ color: '#dc2626' }}>*</span></label>
                                <div 
                                    className="doc-file-dropzone" 
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input 
                                        type="file" 
                                        ref={fileInputRef} 
                                        onChange={handleFileSelect} 
                                        style={{ display: 'none' }} 
                                    />
                                    <UploadIcon style={{ fontSize: '32px', color: '#014486' }} />
                                    {selectedFile ? (
                                        <div>
                                            <span style={{ fontWeight: 700, color: '#0f172a', display: 'block' }}>{selectedFile.name}</span>
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>{(selectedFile.size / 1024).toFixed(1)} KB</span>
                                        </div>
                                    ) : (
                                        <div>
                                            <span style={{ fontWeight: 600, color: '#334155', display: 'block' }}>Click to choose a document</span>
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>PDF, DOCX, PNG, JPG, etc.</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Category & Document Type */}
                            <div className="doc-form-row">
                                <div className="doc-form-group">
                                    <label>Category</label>
                                    <select 
                                        value={uploadCategory} 
                                        onChange={(e) => setUploadCategory(e.target.value)}
                                        className="doc-modal-input"
                                    >
                                        <option value="OTHER">Other Documents</option>
                                        <option value="ACADEMIC">Academic Records</option>
                                        <option value="PERSONAL_ID">Personal ID / Proof</option>
                                        <option value="ASSIGNMENT">Assignments</option>
                                        <option value="STUDY_MATERIAL">Study Material</option>
                                        <option value="CERTIFICATE">Certificates</option>
                                        <option value="FINANCIAL">Financial / Receipts</option>
                                    </select>
                                </div>

                                <div className="doc-form-group">
                                    <label>Document Type</label>
                                    <input 
                                        type="text" 
                                        list="doctype-suggestions"
                                        placeholder="e.g. Aadhar, Degree, Marksheet..." 
                                        value={uploadDocType}
                                        onChange={(e) => setUploadDocType(e.target.value)}
                                        className="doc-modal-input"
                                    />
                                    <datalist id="doctype-suggestions">
                                        <option value="National ID / Aadhar" />
                                        <option value="Degree Certificate" />
                                        <option value="Marksheet / Transcripts" />
                                        <option value="Transfer Certificate (TC)" />
                                        <option value="Bonafide Certificate" />
                                        <option value="Resume / CV" />
                                        <option value="Fee Payment Receipt" />
                                    </datalist>
                                </div>
                            </div>

                            {/* Remarks */}
                            <div className="doc-form-group">
                                <label>Remarks / Notes</label>
                                <textarea 
                                    placeholder="Add any remarks or notes..."
                                    value={uploadRemarks}
                                    onChange={(e) => setUploadRemarks(e.target.value)}
                                    className="doc-modal-textarea"
                                    rows={2}
                                />
                            </div>

                            <div className="doc-modal-footer">
                                <button 
                                    type="button" 
                                    className="doc-btn-secondary" 
                                    onClick={() => setIsUploadModalOpen(false)}
                                    disabled={uploading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="doc-btn-primary"
                                    disabled={!selectedFile || uploading}
                                >
                                    {uploading ? <CircularProgress size={16} style={{ color: 'white' }} /> : 'Start Upload'}
                                </button>
                            </div>
                        </form>
                    </div>
                </>
            )}
        </div>
    );
}
